/**
 * UpcomingRunsList — "Up next": what is queued, at what clock time, for how
 * long. Shared by the all-farms overview and the single-farm page.
 */

import { Timer } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { formatRunTime, type UpcomingRun } from '../../utils/irrigationRuns';
import { formatStartClock } from '../../utils/irrigationClock';
import { useOrgTimezone } from '../../utils/timezone';

interface UpcomingRunsListProps {
  upcoming: UpcomingRun[];
  /** Farm names are noise when the page is already one farm. */
  showFarm?: boolean;
}

export default function UpcomingRunsList({ upcoming, showFarm = false }: UpcomingRunsListProps) {
  const orgTz = useOrgTimezone();
  if (upcoming.length === 0) return null;

  return (
    <div>
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
        <Timer className="h-4 w-4 text-gray-400" />
        Up next
      </h2>
      <Card>
        <CardContent className="divide-y divide-gray-100 p-0">
          {upcoming.map((next) => (
            <div key={next.key} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="h-6 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: next.zoneColor }}
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{next.zoneName}</p>
                  <p className="text-xs text-gray-500">
                    {showFarm && next.farmName ? `${next.farmName} · ` : ''}
                    for {formatRunTime(next.durationSec)}
                  </p>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-semibold text-gray-900 tabular-nums">
                  {formatStartClock(next.startsAtMs, orgTz)}
                </p>
                <p className="text-xs text-gray-500 tabular-nums">
                  in {formatRunTime(next.startsInSec)}
                </p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
